import { ApiResponse } from "@/types/sports";

/**
 * Base API client for making HTTP requests to the backend
 */
export class ApiClient {
  private baseUrl: string;

  constructor() {
    // In production and development, we should use the Next.js API routes
    this.baseUrl = "/api";
    console.log("[ApiClient] Using baseUrl:", this.baseUrl);
  }

  // For testing purposes - enables switching to an external API if needed
  setBaseUrl(url: string) {
    this.baseUrl = url;
    console.log("[ApiClient] Changed baseUrl to:", this.baseUrl);
    return this;
  }

  /**
   * Checks if the user appears to be authenticated based on session storage
   */
  private isAuthenticated(): boolean {
    // For Auth0, we should be relying on the session cookie that is automatically managed
    // This implementation makes API compatible with the Auth0 authentication flow
    return true; // Let the actual auth check happen on the server side via the session cookie
  }

  /**
   * Generic fetch method with proper error handling and typing
   */
  async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // For subscription endpoints, check auth first to avoid unnecessary requests
      if (
        (endpoint.startsWith("/subscription") || 
         endpoint.match(/\/leagues\/\w+\/roster/)) && 
        !this.isAuthenticated()
      ) {
        return {
          success: false,
          data: null as unknown as T,
          error: "User is not authenticated",
        };
      }

      const defaultHeaders: HeadersInit = {
        "Content-Type": "application/json",
      };

      const config: RequestInit = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      };

      // Add authentication if available
      const token = sessionStorage.getItem("auth_token");
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      // Build the full URL
      const url = `${this.baseUrl}${endpoint}`;

      // Make the request
      const response = await fetch(url, config);

      // Handle non-200 responses
      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          data: null as unknown as T,
          error:
            errorData.message ||
            `Error: ${response.status} ${response.statusText}`,
        };
      }

      // Return the successful response
      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      // Handle unexpected errors
      return {
        success: false,
        data: null as unknown as T,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Convenience method for GET requests
   */
  async get<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return this.fetch<T>(endpoint, { ...options, method: "GET" });
  }

  /**
   * Convenience method for POST requests
   */
  async post<T>(
    endpoint: string,
    body: unknown,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  /**
   * Convenience method for PUT requests
   */
  async put<T>(
    endpoint: string,
    body: unknown,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  async delete<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return this.fetch<T>(endpoint, { ...options, method: "DELETE" });
  }
}

// Create a singleton instance for use throughout the app
export const api = new ApiClient();
